import { useState, useCallback, useRef } from "react";
import debounce from "lodash/debounce";
import useDocMembershipQuitApi from "@apis/useDocMembershipQuitApi";
import { parseDate } from "@util/moment";
import { DateTimeStandard } from "@curves/ui-base";
import { isFunction, isEmpty } from "@util/utils";
import useValidationErrors from "@util/hook/useValidationErrors";

const watchFields = ["applyDate", "isExtendEndDate", "reasonType", "isFreeQuitFee", "processingFee"];

const buildRangeFeePayload = d => {
	return {
		membershipID: d.membershipID,
		applyDate: parseDate(d.applyDate, DateTimeStandard.DisplayUtcFull),
		reasonType: d.reasonType,
		contractMonthTerm: d.contractMonthTerm,
		suspendMonth: d.suspendMonth,
		suspendDay: d.suspendDay,
		giftMonth: d.giftMonth,
		giftDay: d.giftDay,
		usedMonth: d.usedMonth,
		usedDay: d.usedDay,
		avgMonthFee: d.avgMonthFee,
		processingFee: d.processingFee,
		unusedExtensionMonth: d.unusedExtensionMonth,
		unusedExtensionDay: d.unusedExtensionDay,
		isFreeQuitFee: d.isFreeQuitFee,
		lastMonthlyFee: d.lastMonthlyFee,
		isExtendEndDate: d.isExtendEndDate,
	};
};

const useMembershipQuitForm = ({ formAdapter, initFormData, onSubmitSuccess, onSubmitError, onSavaDraftSuccess, onSaveDraftError }) => {
	const [formData, setFormData] = useState(initFormData);
	const { validationErrors, updateValidationErrors, clearValidationErrors } = useValidationErrors();

	const submittingRef = useRef(false);
	const lastRangeFeePayload = useRef({});

	const { postDocMembershipQuitRangeFeeApi, postDocMembershipQuitApi, postDocMembershipQuitTemporaryApi } = useDocMembershipQuitApi();

	const calculateRangeFee = async payload => {
		const resp = await postDocMembershipQuitRangeFeeApi(payload);
		if (resp?.isError) {
			updateValidationErrors(resp?.result || {});
		} else if (!isEmpty(resp)) {
			setFormData(prev => ({ ...prev, ...resp }));
		}
	};

	const triggerRangeFee = useCallback(
		debounce(async payload => {
			if (JSON.stringify(payload) === JSON.stringify(lastRangeFeePayload.current)) return;
			lastRangeFeePayload.current = payload;
			calculateRangeFee(payload);
		}, 500),
		[]
	);

	const manualTriggerRangeFee = () => {
		const payload = buildRangeFeePayload({
			...formData,
			...formAdapter.getFormData(),
		});
		calculateRangeFee(payload);
	};

	const handleFieldChange = (key, value) => {
		if (watchFields.some(_key => _key === key)) {
			const payload = buildRangeFeePayload({
				...formData,
				...formAdapter.getFormData(),
				[key]: value,
			});
			triggerRangeFee(payload);
		}
	};

	const handleSubmit = async () => {
		if (submittingRef.current) return;

		if (!formAdapter.validateForm()) {
			window.scrollTo({
				top: 705,
				behavior: "smooth",
			});
			return;
		}

		submittingRef.current = true;
		clearValidationErrors();

		const payload = {
			...formData,
			...formAdapter.getFormData(),
		};

		if (!payload.cardPaymentAmount) payload.cardPaymentAmount = 0;
		if (!payload.cashPaymentAmount) payload.cashPaymentAmount = 0;

		try {
			const resp = await postDocMembershipQuitApi(payload.contractID, payload);
			if (resp?.isError) {
				updateValidationErrors(resp?.result || {});
			} else if (isEmpty(resp)) {
				// 失敗
				isFunction(onSubmitError) && onSubmitError(resp || {});
			} else {
				// 成功
				isFunction(onSubmitSuccess) && onSubmitSuccess(resp || {});
			}
		} catch (err) {
			console.error(err);
		}

		submittingRef.current = false;
	};

	const handleSaveDraft = async () => {
		if (submittingRef.current) return;
		submittingRef.current = true;

		const payload = {
			...formData,
			...formAdapter.getFormData(),
			signImg: "", // 清除簽名圖片，因為暫存不需要
		};

		try {
			const resp = await postDocMembershipQuitTemporaryApi(payload);
			if (resp) {
				// 成功儲存暫存資料
				isFunction(onSavaDraftSuccess) && onSavaDraftSuccess(resp);
			} else {
				// 儲存暫存資料失敗
				isFunction(onSaveDraftError) && onSaveDraftError(resp || {});
			}
		} catch (err) {
			console.error(err);
		}

		submittingRef.current = false;
	};

	return {
		formData,
		updateField: handleFieldChange,
		validationErrors,
		onSubmitForm: handleSubmit,
		onSaveDraft: handleSaveDraft,
		manualTriggerRangeFee,
	};
};

export default useMembershipQuitForm;
